import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth.store'
import {
  ADMIN_ROLES, DASHBOARD_ROLES, SUPPORT_DESK_ROLES, canEnterPanel, hasAnyRole, homePathFor,
} from '../constants/roles'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    /**
     * Who may open the page (add-support-system 9.1). The server is the real gate; this keeps the panel from showing
     * a page that would answer 403. A page with no roles is for administrators only.
     */
    roles?: readonly string[]
  }
}

const ADMIN = { roles: ADMIN_ROLES }
const SUPPORT = { roles: SUPPORT_DESK_ROLES }

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('../layouts/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        // The dashboard: it reads /admin/stats/* now (openspec/changes/admin-operational-audit). It used to call
        // /analytics/* endpoints that never existed, which is why this pointed at /providers from 2026-09-19.
        redirect: '/dashboard',
      },
      {
        // An agent sees only the support report card here (add-support-system 13.4).
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/dashboard/DashboardView.vue'),
        meta: { roles: DASHBOARD_ROLES },
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('../views/users/UsersList.vue'),
        meta: ADMIN,
      },
      {
        path: 'providers',
        name: 'Providers',
        component: () => import('../views/providers/ProvidersList.vue'),
        meta: ADMIN,
      },
      {
        path: 'providers/:id',
        name: 'ProviderDetails',
        component: () => import('../views/providers/ProviderDetails.vue'),
        meta: ADMIN,
      },
      {
        path: 'reviews',
        name: 'Reviews',
        component: () => import('../views/reviews/ReviewModeration.vue'),
        meta: ADMIN,
      },
      {
        path: 'promotions',
        name: 'Promotions',
        component: () => import('../views/promotions/PromotionsPage.vue'),
        meta: ADMIN,
      },
      {
        path: 'bookings',
        name: 'Bookings',
        component: () => import('../views/bookings/BookingsList.vue'),
        meta: ADMIN,
      },
      {
        // The platform's one cancellation policy (openspec/changes/centralize-cancellation-policy; AdminOnly).
        path: 'cancellation-policy',
        name: 'CancellationPolicy',
        component: () => import('../views/cancellation-policy/CancellationPolicyPage.vue'),
        meta: ADMIN,
      },
      {
        // Reported problems with a paid visit (openspec/changes/add-booking-issue 7.1–7.2; Admin/SysAdmin/Finance on
        // the server): the queue, and one issue's page where an administrator decides it.
        path: 'booking-issues',
        name: 'BookingIssues',
        component: () => import('../views/booking-issues/BookingIssuesPage.vue'),
        meta: ADMIN,
      },
      {
        path: 'booking-issues/:id',
        name: 'BookingIssueDetail',
        component: () => import('../views/booking-issues/BookingIssueDetail.vue'),
        meta: ADMIN,
      },
      {
        // The salons' payouts (openspec/changes/add-provider-payouts 6.2; Admin/SysAdmin/Finance on the server): the
        // queue, and its steps — approve, reject, record the transfer or its failure — and the audited «نمایش شبا».
        path: 'payouts',
        name: 'Payouts',
        component: () => import('../views/payouts/PayoutsPage.vue'),
        meta: ADMIN,
      },
      {
        // Finance's money operations (openspec/changes/add-money-operations-visibility 5.2; Admin/SysAdmin/Finance on
        // the server, the same guard as the payouts): one page with tabs (`?tab=`), and one payment with everything
        // about it.
        path: 'money',
        name: 'Money',
        component: () => import('../views/money/MoneyPage.vue'),
        meta: ADMIN,
      },
      {
        path: 'money/payments/:id',
        name: 'MoneyPayment',
        component: () => import('../views/money/PaymentDetailPage.vue'),
        meta: ADMIN,
      },
      // «سفارشات» was a placeholder at /orders; the platform's orders are its bookings.
      { path: 'orders', redirect: '/bookings' },
      {
        // The support desk (openspec/changes/add-support-system §9): the queue, one ticket's workspace, the shared
        // saved replies and the help articles. Admin, Administrator and Support on the server.
        path: 'support',
        name: 'SupportQueue',
        component: () => import('../views/support/SupportQueuePage.vue'),
        meta: SUPPORT,
      },
      {
        path: 'support/tickets/:id',
        name: 'SupportTicket',
        component: () => import('../views/support/SupportTicketPage.vue'),
        meta: SUPPORT,
      },
      {
        path: 'support/saved-replies',
        name: 'SupportSavedReplies',
        component: () => import('../views/support/SavedRepliesPage.vue'),
        meta: SUPPORT,
      },
      {
        path: 'support/help-articles',
        name: 'SupportHelpArticles',
        component: () => import('../views/support/HelpArticlesPage.vue'),
        meta: SUPPORT,
      },
      {
        path: 'logs',
        name: 'Logs',
        component: () => import('../views/logs/LogsView.vue'),
        meta: ADMIN,
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
  },
]

/**
 * May someone with `roles` open a page (add-support-system 9.1)?
 * - `refuse`: they are not staff at all — back to the sign-in page.
 * - `home`: staff, but not for this page — to their own landing page (the support queue for an agent).
 * - `allow`.
 * A page inside the panel with no roles of its own is for administrators only.
 */
export function decideAccess(
  to: { matched: readonly { meta?: { roles?: readonly string[] } }[] },
  roles: readonly string[] | null | undefined,
): 'allow' | 'home' | 'refuse' {
  if (!canEnterPanel(roles)) return 'refuse'
  const pageRoles = to.matched.length > 1 ? to.matched[to.matched.length - 1]?.meta?.roles ?? ADMIN_ROLES : undefined
  const allowed = to.matched.every((r) => !r.meta?.roles || hasAnyRole(roles, r.meta.roles))
    && (!pageRoles || hasAnyRole(roles, pageRoles))
  return allowed ? 'allow' : 'home'
}

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Allow access to login page without auth
  if (to.meta.requiresAuth === false) {
    // Only staff are sent on: someone the panel refused keeps the sign-in page instead of bouncing back and forth.
    if (authStore.isAuthenticated && authStore.canEnterPanel && to.name === 'Login') {
      next(authStore.homePath)
    } else {
      next()
    }
    return
  }

  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  // Fetch user data if not already loaded
  if (!authStore.user) {
    await authStore.fetchCurrentUser()
  }

  const decision = decideAccess(to, authStore.roles)
  if (decision === 'refuse') {
    console.error('Access denied: no staff role', { roles: authStore.roles })
    next({ name: 'Login' })
    return
  }
  if (decision === 'home') {
    // A page this role does not use (e.g. an agent on a money page): their own landing page instead.
    next(homePathFor(authStore.roles))
    return
  }

  next()
})

export default router
